
// MARK: Api
import * as api from "@startapp/mistral-admin-api";

// MARK: Mobx
import { observable, action, computed } from "mobx";

// MARK: Components
import { IRowItem, ColumnType } from "../../components/Table/TableRow";
import ReactQuill from "react-quill";

// MARK: Stores
import VariableChangeHandler from "../../resources/VariableChangeHandler";
import { uiStore, routerStore, languageStore } from "../_rootStore";
import ImageService from "../../services/ImageService";

// MARK: Resources
import strings from "../../resources/strings";

export default class OfferStore extends VariableChangeHandler {
	// Services
	public imageService: ImageService = new ImageService();

	// Control
	@observable public loading: boolean = false;
	@observable public onEditMode: boolean = false;
	@observable public pageOffSet: number = 0;

	@observable public offers: api.Offer[] = [];
	@observable public selectedOffer: api.Offer | null = null;

	// Variables
	@observable public name: string = "";
	@observable public nick: string = "";
	@observable public description: string = "";
	@observable public descriptionHtml: string = "";
	@observable public url: string = "";

	@computed
	public get header() {
		const header = strings.offers.table.header;
		return [
			header.picture,
			header.nick,
			header.name,
			header.description,
		];
	}

	private static formatOffer(offer: api.Offer): IRowItem {
		const offerInSelectedLanguage = offer.languages.filter((offerLanguage) =>
			offerLanguage.language === languageStore.selectedLanguage)[0].translation;

		return {
			id: offer.id,
			name: offerInSelectedLanguage.name,
			columns: [
				{
					value: offerInSelectedLanguage.image.url,
					type: ColumnType.avatar,
				},
				{
					value: offerInSelectedLanguage.nick,
				},
				{
					value: offerInSelectedLanguage.name,
				},
				{
					value: offerInSelectedLanguage.description,
				},
			],
		};
	}

	@computed
	public get offersTableRows(): IRowItem[] {
		return this.offers.map(OfferStore.formatOffer);
	}

	@action
	public getOffer = async (offerId: string) => {
		if (this.loading) {
			return;
		}

		this.loading = true;

		try {
			this.selectedOffer = await api.getOffer(offerId);
			this.setEditorFields(this.selectedOffer);
		} catch (e) {
			uiStore.showErrorSnackbar(e);
		} finally {
			this.loading = false;
		}
	};

	@action
	public getOffers = async (pageOffSet?: number) => {
		if (this.loading) {
			return;
		}

		if (!pageOffSet) {
			pageOffSet = 0;
		}

		this.loading = true;

		try {
			this.offers = await api.getOffers(pageOffSet);
			this.pageOffSet = pageOffSet;
		} catch (e) {
			uiStore.showErrorSnackbar(e);
		} finally {
			this.loading = false;
		}
	};

	@action
	public createOrEditOffer = async () => {
		if (this.loading) {
			return;
		}

		this.loading = true;

		try {
			if (this.imageService.singleImageUploader && this.imageService.singleImageUploader.uploadedUncertainImage) {
				if (!this.selectedOffer) {
					const newOffer: api.NewOffer = {
						name: this.name,
						nick: this.nick,
						description: this.description !== "" ? this.description : null,
						descriptionHtml: this.descriptionHtml !== "" ? this.descriptionHtml : null,
						url: this.url ? this.url : null,
						image: this.imageService.singleImageUploader.uploadedUncertainImage,
					};
					this.selectedOffer = await api.createOffer(newOffer);
				} else {
					const editedOffer: api.EditOffer = {
						language: languageStore.selectedLanguage,
						nick: this.nick,
						name: this.name,
						description: this.description !== "" ? this.description : null,
						descriptionHtml: this.descriptionHtml !== "" ? this.descriptionHtml : null,
						url: this.url,
						image: this.imageService.singleImageUploader.uploadedUncertainImage,
					};
					this.selectedOffer = await api.editOffer(this.selectedOffer.id, editedOffer);
				}
				routerStore.replace("/dashboard/offers");
				window.location.reload();
			} else {
				uiStore.showSnackbar(strings.offers.createOrEdit.errors.imageNotFound);
			}
		} catch (e) {
			uiStore.showErrorSnackbar(e);
		} finally {
			this.loading = false;
		}
	};

	@action
	public deleteOffer = async (offerId: string) => {
		if (this.loading) {
			return;
		}

		this.loading = true;

		try {
			await api.deleteOffer(offerId);
			this.offers = this.offers.filter((offer) => offer.id !== offerId);
		} catch (e) {
			uiStore.showErrorSnackbar(e);
		} finally {
			this.loading = false;
		}
	};

	@action
	public openDeleteOfferDialog = async (offerId: string, offerName: string) => {
		uiStore.showDialog({
			title: strings.offers.deleteOfferDialog.title,
			message: `${strings.offers.deleteOfferDialog.message}: ${offerName}`,
		}, () => this.deleteOffer(offerId));
	};

	@action
	public nextPage = async () => {
		await this.getOffers(this.pageOffSet + 1);
	};

	@action
	public previousPage = async () => {
		if (this.pageOffSet > 0) {
			await this.getOffers(this.pageOffSet - 1);
		}
	};

	@action
	public clear = () => {
		this.imageService.clear();
		this.name = "";
		this.nick = "";
		this.description = "";
		this.descriptionHtml = "";
		this.url = "";
	};

	@action
	public setEditorFields = (offer: api.Offer) => {
		const offerInSelectedLanguage = offer.languages.filter((offerLanguage) =>
			offerLanguage.language === languageStore.selectedLanguage)[0].translation;

		this.imageService.setSingleImage(offerInSelectedLanguage.image);
		this.name = offerInSelectedLanguage.name;
		this.nick = offerInSelectedLanguage.nick;
		this.description = offerInSelectedLanguage.description || "";
		this.descriptionHtml = offerInSelectedLanguage.descriptionHtml || "" ;
		this.url = offerInSelectedLanguage.url ? offerInSelectedLanguage.url : "";
	};

	@action
	public startOfferCreateOrEdit(offerId?: string) {
		if (offerId) {
			this.getOffer(offerId);
			routerStore.push(`/dashboard/offers/editor/${offerId}`);
		} else {
			this.selectedOffer = null;
			this.clear();
			routerStore.push("/dashboard/offers/editor/");
		}
	}

	public quill: ReactQuill | null = null;

	@computed
	public get editorModules() {
		return {
			toolbar: {
				// Buttons we want at the Quill's editor toolbar.
				container: [
					[{ header: [1, 2, false] }],
					["bold", "italic", "underline", "strike", "blockquote"],
					[
						{ list: "ordered" },
						{ list: "bullet" },
						{ indent: "-" },
						{ indent: "+1" },
					],
					["link", "image"],
				],
			},
		};
	}

	public setQuillRef = (el: ReactQuill | null) => {
		this.quill = el;
	}

	@action
	public handleBodyHtmlChange = (content: string) => {
		this.descriptionHtml = content;
	}
}
